import { useEffect, useRef, useState } from 'react';

/**
 * Follows `active`, but late to turn on and slow to turn off.
 *
 * A loading indicator driven straight off a pending flag is worse than none: the
 * overwhelmingly common navigation in this app is to an already-loaded chunk,
 * which is pending for a frame or two, so an undelayed indicator strobes on every
 * click. Two windows fix that, and they are separate decisions:
 *
 * - `delayMs` — how long `active` must hold before the flag turns on. A
 *   navigation that finishes inside this window never shows anything at all.
 * - `minVisibleMs` — how long the flag stays on once it HAS turned on, even if
 *   `active` has already gone false. Without it, a navigation that lands just
 *   past the delay flashes the indicator for a few milliseconds, which reads as
 *   a glitch rather than as feedback.
 */
export function useDelayedFlag(
  active: boolean,
  { delayMs, minVisibleMs }: { delayMs: number; minVisibleMs: number }
): boolean {
  const [on, setOn] = useState(false);
  const shownAtRef = useRef(0);

  useEffect(() => {
    if (active) {
      // Already on: nothing to schedule. Re-entering `active` during the
      // minimum-visible window simply keeps it on.
      if (on) return;
      const timer = setTimeout(() => {
        shownAtRef.current = Date.now();
        setOn(true);
      }, delayMs);
      return () => clearTimeout(timer);
    }

    if (!on) return;
    const remaining = minVisibleMs - (Date.now() - shownAtRef.current);
    if (remaining <= 0) {
      setOn(false);
      return;
    }
    const timer = setTimeout(() => setOn(false), remaining);
    return () => clearTimeout(timer);
  }, [active, on, delayMs, minVisibleMs]);

  return on;
}
